import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { employeeApi, offboardingApi, docusignApi, extractErrorMessage } from '../../../services/hrApi';
import type { Employee } from '../../../types';
import ConfirmationModal from '../../../components/ConfirmationModal';
import './StepStyles.scss';
import { Editor } from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
// @ts-ignore
import draftToHtml from 'draftjs-to-html';
// @ts-ignore
import htmlToDraft from 'html-to-draftjs';
import jsPDF from 'jspdf';

interface DocumentGenerationStepProps {
  employeeId?: string;
  data?: any;
  onDataChange: (data: any) => void;
  onComplete: (isComplete: boolean) => void;
}

type ActionType = 'upload' | 'generate';

const DocumentGenerationStep: React.FC<DocumentGenerationStepProps> = ({
  employeeId,
  data,
  onDataChange,
  onComplete,
}) => {
  const [activeAction, setActiveAction] = useState<ActionType>('upload');
  const [showGenerateEditor, setShowGenerateEditor] = useState<boolean>(false);
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [terminationLetter, setTerminationLetter] = useState<{
    id?: string;
    fileUrl?: string;
    fileName?: string;
    fileKey?: string;
    uploadedAt?: string;
    generated?: boolean;
  }>({});

  const [documentContent, setDocumentContent] = useState<EditorState>(EditorState.createEmpty());

  // DocuSign envelope state (reuse logic pattern from Offboarding DocumentPackageStep)
  const [existingEnvelope, setExistingEnvelope] = useState<any>(null);
  const [checkingEnvelope, setCheckingEnvelope] = useState(false);
  const [creatingEnvelope, setCreatingEnvelope] = useState(false);

  useEffect(() => {
    if (employeeId) {
      fetchEmployee();
      fetchDocuments();
      checkExistingEnvelope();
    }
  }, [employeeId]);

  const onCompleteRef = useRef(onComplete);
  const onDataChangeRef = useRef(onDataChange);
  
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onDataChangeRef.current = onDataChange;
  }, [onComplete, onDataChange]);

  const isCompleteRef = useRef(false);
  const prevDocumentRef = useRef(terminationLetter);
  
  useEffect(() => {
    const isComplete = !!terminationLetter.fileUrl;
    
    const documentChanged = 
      prevDocumentRef.current.fileUrl !== terminationLetter.fileUrl ||
      prevDocumentRef.current.fileName !== terminationLetter.fileName ||
      prevDocumentRef.current.id !== terminationLetter.id;
    
    if (documentChanged) {
      prevDocumentRef.current = terminationLetter;
      
      if (isComplete !== isCompleteRef.current) {
        isCompleteRef.current = isComplete;
        onCompleteRef.current(isComplete);
      }
      
      onDataChangeRef.current({
        termination_letter: terminationLetter,
      });
    }
  }, [terminationLetter]);

  const fetchEmployee = async () => {
    if (!employeeId) return;
    try {
      setLoading(true);
      const emp = await employeeApi.getEmployee(employeeId);
      setEmployee(emp);
    } catch (error) {
      console.error('Error fetching employee:', error);
      toast.error('Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const fetchDocuments = async () => {
    if (!employeeId) return;
    try {
      const docs = await employeeApi.getEmployeeDocuments(employeeId);
      let terminationLetter: any = {};

      if (Array.isArray(docs) && docs.length > 0) {
        const termDoc = docs.find((doc: any) => doc.type === 'termination_letter');
        if (termDoc) {
          terminationLetter = {
            id: termDoc.id,
            fileUrl: termDoc.fileUrl,
            fileName: termDoc.name,
            fileKey: termDoc.path,
            uploadedAt: termDoc.updated_at || termDoc.created_at,
            generated: false,
          };
        }
      }

      if (!terminationLetter.fileUrl && data) {
        terminationLetter = data?.termination_letter || {};
      }

      setTerminationLetter(terminationLetter);
    } catch (error) {
      console.error('Error fetching documents:', error);
      if (data) {
        setTerminationLetter(data?.termination_letter || {});
      }
    }
  };

  const getS3Path = (fileName: string): string => {
    let userId = 'unknown';
    const userDataString = localStorage.getItem('userdata');
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        userId = userData?.id || userData?.user_id || userData?.uid || 'unknown';
      } catch (e) {
        console.warn('Failed to parse userdata:', e);
      }
    }
    
    if (userId === 'unknown') {
      const authUser = localStorage.getItem('authUser');
      if (authUser) {
        userId = authUser;
      }
    }

    const extension = fileName.split('.').pop() || 'pdf';
    const fileNameWithEmployeeId = `Termination Letter-${employeeId}.${extension}`;
    
    return `HR Portal/${userId}/Offboarding Documents/${employeeId}/${fileNameWithEmployeeId}`;
  };

  const checkExistingEnvelope = async () => {
    if (!employeeId) return;
    setCheckingEnvelope(true);
    try {
      const result = await docusignApi.getEnvelopeDraft(employeeId as string, 'employee-offboarding');
      if (result.exists) {
        setExistingEnvelope(result);
      } else {
        setExistingEnvelope(null);
      }
    } catch (error) {
      console.error('Error checking existing envelope for termination letter:', error);
      setExistingEnvelope(null);
    } finally {
      setCheckingEnvelope(false);
    }
  };

  const handleDeleteDraft = async (): Promise<boolean> => {
    if (!employeeId) return false;

    if (!window.confirm('Are you sure you want to delete the existing DocuSign envelope draft? You will need to recreate it in the Document Package step.')) {
      return false;
    }

    setCreatingEnvelope(true);
    try {
      const result = await docusignApi.deleteEnvelopeDraft({
        employee_id: employeeId,
        envelope_type: 'employee-offboarding',
      });

      if (result.success) {
        toast.success('Envelope draft deleted. You can now change the termination letter and recreate the envelope from the Document Package step.');
        setExistingEnvelope(null);
        return true;
      }

      toast.error(`Failed to delete envelope draft: ${result.error || 'Unknown error'}`);
      return false;
    } catch (error: any) {
      console.error('Error deleting envelope draft:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to delete envelope draft: ${errorMessage}`);
      return false;
    } finally {
      setCreatingEnvelope(false);
    }
  };

  const ensureEnvelopeAllowsDocumentChange = async (actionDescription: string): Promise<boolean> => {
    const envelopeStatus = existingEnvelope?.status;

    if (!existingEnvelope || envelopeStatus === 'voided') {
      return true;
    }

    if (envelopeStatus === 'created' || envelopeStatus === 'tabs_placed') {
      const label =
        envelopeStatus === 'created'
          ? 'Created'
          : 'Tabs Placed';

      if (
        !window.confirm(
          `A DocuSign envelope draft exists for this employee (status: ${label}). ` +
          `${actionDescription} will require deleting the existing draft. ` +
          `You will need to recreate the envelope from the Document Package step. Do you want to continue?`
        )
      ) {
        return false;
      }

      const draftDeleted = await handleDeleteDraft();
      return draftDeleted;
    }

    if (envelopeStatus === 'sent' || envelopeStatus === 'completed') {
      toast.error(
        `There is an active DocuSign envelope for this employee (status: ${envelopeStatus}). ` +
        `${actionDescription} is not allowed while the envelope is active. ` +
        'Please void or delete the envelope from the Document Package step and then try again.'
      );
      return false;
    }

    toast.error(
      `There is a DocuSign envelope for this employee (status: ${envelopeStatus}). ` +
      `${actionDescription} is not allowed while the envelope is active. ` +
      'Please manage the envelope from the Document Package step.'
    );
    return false;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !employeeId) return;

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size exceeds maximum allowed size of 10MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['.pdf', '.doc', '.docx'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      toast.error(`Invalid file type. Allowed types: ${allowedTypes.join(', ')}`);
      return;
    }

    // Ensure envelope state allows replacing the termination letter
    const canChange = await ensureEnvelopeAllowsDocumentChange('Replacing the termination letter file');
    if (!canChange) {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('employee_id', employeeId);
      formData.append('document_type', 'termination_letter');
      
      const s3Path = getS3Path(file.name);
      formData.append('s3_path', s3Path);
      
      const currentDoc = terminationLetter;
      if (currentDoc.fileKey) {
        formData.append('old_file_path', currentDoc.fileKey);
      }

      const result = await offboardingApi.uploadDocument(formData);

      setTerminationLetter({
        id: result.documentId,
        fileUrl: result.fileUrl,
        fileName: result.fileName || file.name,
        fileKey: result.fileKey,
        uploadedAt: new Date().toISOString(),
        generated: false,
      });

      setShowGenerateEditor(false);
      toast.success('Termination letter uploaded successfully!');
    } catch (error: any) {
      console.error('Error uploading termination letter:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to upload file: ${errorMessage}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const generateTerminationLetterTemplate = (): string => {
    const employeeName = employee ? `${employee.first_name} ${employee.last_name}` : 'Employee';
    const role = employee?.position || 'Position';
    const department = employee?.department || 'Department';
    const hireDate = employee?.hire_date ? new Date(employee.hire_date).toLocaleDateString() : 'N/A';
    const terminationDate = new Date().toLocaleDateString();

    const template = `
TERMINATION LETTER

Date: ${terminationDate}

Dear ${employeeName},

This letter is to inform you that your employment with [Company Name] will be terminated effective ${terminationDate}.

EMPLOYEE INFORMATION:
- Name: ${employeeName}
- Position: ${role}
- Department: ${department}
- Hire Date: ${hireDate}
- Termination Date: ${terminationDate}

TERMINATION DETAILS:
- Your last day of work will be ${terminationDate}.
- All company property, including keys, access cards, equipment, and confidential materials, must be returned by your last day.
- Your final paycheck will be processed according to company policy and applicable state laws.

BENEFITS AND FINAL PAY:
- Any accrued but unused vacation time will be paid out according to company policy.
- Information regarding continuation of health insurance coverage (COBRA) will be sent to you separately.
- Your final paycheck will include all wages earned through your termination date.

NEXT STEPS:
1. Return all company property by ${terminationDate}.
2. Complete any pending work assignments and hand over responsibilities.
3. Ensure all company accounts and access are properly closed.

We thank you for your service and wish you the best in your future endeavors.

Sincerely,
[HR Manager Name]
[Company Name]

Employee Acknowledgment:
I acknowledge receipt of this termination letter.

Signature: _________________________ Date: _____________
${employeeName}
    `.trim();

    return template;
  };

  const stripHtmlTags = (html: string): string => {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    let text = tmp.textContent || tmp.innerText || '';
    text = text.replace(/\s+/g, ' ');
    text = text.replace(/&nbsp;/g, ' ');
    text = text.replace(/&amp;/g, '&');
    text = text.replace(/&lt;/g, '<');
    text = text.replace(/&gt;/g, '>');
    text = text.replace(/&quot;/g, '"');
    text = text.replace(/&#39;/g, "'");
    return text.trim();
  };

  const createPDFFromContent = async (editorState: EditorState): Promise<Blob> => {
    const htmlContent = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    const tmp = document.createElement('DIV');
    tmp.innerHTML = htmlContent;
    
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });
    
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = margin;
    const lineHeight = 7;
    
    const addText = (text: string, fontSize: number = 12, isBold: boolean = false, isItalic: boolean = false) => {
      pdf.setFontSize(fontSize);
      pdf.setFont('helvetica', isBold ? 'bold' : isItalic ? 'italic' : 'normal');
      const lines = pdf.splitTextToSize(text, maxWidth);
      if (yPosition + (lines.length * lineHeight) > pageHeight - margin) {
        pdf.addPage();
        yPosition = margin;
      }
      lines.forEach((line: string) => {
        pdf.text(line, margin, yPosition);
        yPosition += lineHeight;
      });
    };
    
    addText('TERMINATION LETTER', 16, true);
    yPosition += 5;
    
    const pElements = tmp.querySelectorAll('p');
    if (pElements.length > 0) {
      pElements.forEach((p) => {
        const text = p.textContent?.trim() || '';
        if (text) {
          addText(text, 12);
          yPosition += 2;
        }
      });
    } else {
      const plainText = stripHtmlTags(htmlContent);
      const lines = plainText.split('\n').filter(line => line.trim());
      lines.forEach(line => {
        addText(line.trim(), 12);
        yPosition += 2;
      });
    }
    
    const pdfBlob = pdf.output('blob');
    return pdfBlob;
  };

  const handleGenerateTemplate = () => {
    if (!employeeId || !employee) {
      toast.error('Employee information is required to generate document');
      return;
    }

    const templateContent = generateTerminationLetterTemplate();
    const htmlContent = templateContent
      .split('\n')
      .map(line => line.trim() === '' ? '<br>' : `<p>${line}</p>`)
      .join('');

    const blocksFromHtml = htmlToDraft(htmlContent);
    const { contentBlocks, entityMap } = blocksFromHtml;
    const contentState = ContentState.createFromBlockArray(contentBlocks, entityMap);
    const editorState = EditorState.createWithContent(contentState);

    setDocumentContent(editorState);
    setActiveAction('generate');
  };

  const handleSaveDocument = async () => {
    if (!employeeId || !employee) {
      toast.error('Employee information is required to save document');
      return;
    }

    const currentContent = documentContent.getCurrentContent();
    const plainText = currentContent.getPlainText();
    if (!plainText || plainText.trim() === '') {
      toast.error('Please generate or enter document content before saving');
      return;
    }

    // Ensure envelope state allows regenerating the termination letter
    const canChange = await ensureEnvelopeAllowsDocumentChange('Regenerating the termination letter');
    if (!canChange) {
      return;
    }

    setUploading(true);

    try {
      const pdfBlob = await createPDFFromContent(documentContent);
      const fileName = `Termination Letter-${employeeId}.pdf`;
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      const formData = new FormData();
      formData.append('file', pdfFile);
      formData.append('employee_id', employeeId);
      formData.append('document_type', 'termination_letter');
      
      const s3Path = getS3Path(fileName);
      formData.append('s3_path', s3Path);
      
      const currentDoc = terminationLetter;
      if (currentDoc.fileKey) {
        formData.append('old_file_path', currentDoc.fileKey);
      }

      const result = await offboardingApi.uploadDocument(formData);

      setTerminationLetter({
        id: result.documentId,
        fileUrl: result.fileUrl,
        fileName: result.fileName || fileName,
        fileKey: result.fileKey,
        uploadedAt: new Date().toISOString(),
        generated: true,
      });

      setShowGenerateEditor(false);
      toast.success('Termination letter saved successfully!');
    } catch (error: any) {
      console.error('Error saving termination letter:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to save document: ${errorMessage}`);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async () => {
    setDeleting(true);
    try {
      if (terminationLetter.id) {
        await employeeApi.deleteEmployeeDocument(terminationLetter.id);
      }
      
      setTerminationLetter({});
      setDocumentContent(EditorState.createEmpty());
      setShowGenerateEditor(false);
      setShowDeleteModal(false);
      toast.success('Termination letter deleted successfully');
    } catch (error: any) {
      console.error('Error deleting document:', error);
      const errorMessage = extractErrorMessage(error);
      toast.error(`Failed to delete document: ${errorMessage}`);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="wizard-step">
        <div className="step-header">
          <h4>Document Generation</h4>
          <p>Generate termination letter</p>
        </div>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>Loading employee information...</p>
        </div>
      </div>
    );
  }

  if (!employeeId) {
    return (
      <div className="wizard-step">
        <div className="step-header">
          <h4>Document Generation</h4>
          <p>Generate termination letter</p>
        </div>
        <div className="step-info-box">
          <i className="bx bx-info-circle"></i>
          <p>Employee ID is required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-step">
      <div className="step-header">
        <h4>Document Generation</h4>
        <p>Generate termination letter</p>
      </div>

      <div className="step-form">
        <input
          ref={(el) => (fileInputRef.current = el)}
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />
        
        {terminationLetter.fileUrl ? (
          <div className="document-status">
            <div className="document-status-header">
              {uploading ? (
                <>
                  <i className="bx bx-loader-alt bx-spin status-icon"></i>
                  <h5>Termination Letter - Uploading...</h5>
                </>
              ) : (
                <>
                  <i className="bx bx-check-circle status-icon"></i>
                  <h5>Termination Letter {terminationLetter.generated ? 'Generated' : 'Uploaded'}</h5>
                </>
              )}
            </div>
            <div className="document-info">
              <p><strong>File:</strong> {terminationLetter.fileName}</p>
              {terminationLetter.uploadedAt && (
                <p><strong>Date:</strong> {new Date(terminationLetter.uploadedAt).toLocaleDateString()}</p>
              )}
            </div>
            <div className="document-actions">
              <button
                onClick={async () => {
                  if (terminationLetter.fileKey) {
                    try {
                      const result = await offboardingApi.getDocumentDownloadUrl(terminationLetter.fileKey, 3600);
                      if (result.fileUrl) {
                        window.open(result.fileUrl, '_blank');
                      } else {
                        toast.error('Failed to get download URL');
                      }
                    } catch (error: any) {
                      console.error('Error getting download URL:', error);
                      toast.error('Failed to download document');
                    }
                  }
                }}
                className="btn-view-document"
              >
                <i className="bx bx-download"></i>
                Download Document
              </button>
              <button
                type="button"
                className="btn-replace-document"
                onClick={() => {
                  setActiveAction('upload');
                  setShowGenerateEditor(false);
                  fileInputRef.current?.click();
                }}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <i className="bx bx-loader-alt bx-spin"></i>
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="bx bx-upload"></i>
                    Replace File
                  </>
                )}
              </button>
              {showGenerateEditor ? (
                <button
                  type="button"
                  className="btn-replace-document"
                  onClick={() => setShowGenerateEditor(false)}
                  disabled={uploading}
                >
                  <i className="bx bx-x"></i>
                  Cancel
                </button>
              ) : (
                <button
                  type="button"
                  className="btn-generate-document"
                  onClick={() => {
                    setShowGenerateEditor(true);
                    handleGenerateTemplate();
                  }}
                  disabled={uploading}
                >
                  <i className="bx bx-wand"></i>
                  Generate
                </button>
              )}
              <button
                type="button"
                className="btn-delete-document"
                onClick={() => setShowDeleteModal(true)}
                disabled={uploading}
              >
                <i className="bx bx-trash"></i>
                Delete
              </button>
            </div>
            
            {showGenerateEditor && (
              <div style={{ marginTop: '24px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                <h5 style={{ marginBottom: '16px', fontSize: '16px', fontWeight: 600 }}>
                  Edit & Generate Document
                </h5>
                <div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
                  {!documentContent.getCurrentContent().getPlainText().trim() ? (
                    <button
                      className="btn-generate-template"
                      onClick={handleGenerateTemplate}
                      disabled={uploading || !employee}
                    >
                      <i className="bx bx-wand"></i>
                      Generate Template
                    </button>
                  ) : null}
                  {documentContent.getCurrentContent().getPlainText().trim() && (
                    <button
                      className="btn-save-document"
                      onClick={handleSaveDocument}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <>
                          <i className="bx bx-loader-alt bx-spin"></i>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="bx bx-save"></i>
                          Save Document
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                <div style={{ border: '1px solid #ddd', borderRadius: '4px', background: '#fff' }}>
                  <Editor
                    editorState={documentContent}
                    onEditorStateChange={setDocumentContent}
                    toolbarClassName="toolbarClassName"
                    wrapperClassName="wrapperClassName"
                    editorClassName="editorClassName"
                    placeholder="Click 'Generate Template' to create a template, then edit it and click 'Save Document'"
                    readOnly={uploading}
                  />
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="document-actions-selector">
            <div className="action-tabs">
              <button
                className={`action-tab ${activeAction === 'upload' ? 'active' : ''}`}
                onClick={() => setActiveAction('upload')}
              >
                <i className="bx bx-upload"></i>
                Upload
              </button>
              <button
                className={`action-tab ${activeAction === 'generate' ? 'active' : ''}`}
                onClick={() => {
                  setActiveAction('generate');
                  setShowGenerateEditor(true);
                  handleGenerateTemplate();
                }}
              >
                <i className="bx bx-edit"></i>
                Generate Here
              </button>
            </div>

            <div className="action-content">
              {activeAction === 'upload' ? (
                <div className="upload-section">
                  <div className="upload-area">
                    <i className="bx bx-cloud-upload upload-icon"></i>
                    <h5>Upload Termination Letter</h5>
                    <p>Select a file to upload (PDF, DOC, or DOCX)</p>
                    <button
                      className="btn-upload"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      {uploading ? (
                        <>
                          <i className="bx bx-loader-alt bx-spin"></i>
                          Uploading...
                        </>
                      ) : (
                        <>
                          <i className="bx bx-folder-open"></i>
                          Choose File
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="generate-section">
                  <div className="generate-info">
                    <i className="bx bx-edit-alt generate-icon"></i>
                    <h5>Generate Termination Letter</h5>
                    <p>
                      Generate a new termination letter document based on the employee's information.
                      You can edit the template before saving.
                    </p>
                  </div>
                  
                  <div className="document-editor-container" style={{ marginTop: '20px' }}>
                    <div style={{ marginBottom: '10px', display: 'flex', gap: '10px' }}>
                      {!documentContent.getCurrentContent().getPlainText().trim() ? (
                        <button
                          className="btn-generate-template"
                          onClick={handleGenerateTemplate}
                          disabled={uploading || !employee}
                        >
                          <i className="bx bx-wand"></i>
                          Generate Template
                        </button>
                      ) : null}
                      {documentContent.getCurrentContent().getPlainText().trim() && (
                        <button
                          className="btn-save-document"
                          onClick={handleSaveDocument}
                          disabled={uploading}
                        >
                          {uploading ? (
                            <>
                              <i className="bx bx-loader-alt bx-spin"></i>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="bx bx-save"></i>
                              Save Document
                            </>
                          )}
                        </button>
                      )}
                    </div>
                    
                    <div style={{ border: '1px solid #ddd', borderRadius: '4px' }}>
                      <Editor
                        editorState={documentContent}
                        onEditorStateChange={setDocumentContent}
                        toolbarClassName="toolbarClassName"
                        wrapperClassName="wrapperClassName"
                        editorClassName="editorClassName"
                        placeholder="Click 'Generate Template' to create a template, then edit it and click 'Save Document'"
                        readOnly={uploading}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Delete Termination Letter"
        message={`Are you sure you want to delete the termination letter? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        icon="bx bx-trash"
        onConfirm={handleDeleteDocument}
        onCancel={() => setShowDeleteModal(false)}
        isLoading={deleting}
      />
    </div>
  );
};

export default DocumentGenerationStep;

